import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '@/lib/auth-context';
import type { AiTool, AiToolsCatalogResponse, PortalRole } from '@/types/aiTools';
import { ROLE_SLOT_CONFIG, hasUniversalSlots } from '@/types/aiTools';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

type RoleAiMarketplaceProps = {
  role: PortalRole;
  title: string;
  description: string;
  /** Custom credit balance loader (optional) */
  creditsAvailable?: number | null;
  creditsLoading?: boolean;
};

type ToolsByType = {
  ai: AiTool[];
  gec0: AiTool[];
};

export function RoleAiMarketplace({
  role,
  title,
  description,
  creditsAvailable = null,
  creditsLoading = false,
}: RoleAiMarketplaceProps) {
  const { session } = useAuthContext() as { session: { user?: { id: string }; access_token?: string } | null };
  const accessToken = session?.access_token ?? null;

  const config = ROLE_SLOT_CONFIG[role];
  const isUniversal = hasUniversalSlots(role);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [allTools, setAllTools] = useState<AiTool[]>([]);
  const [activeSlots, setActiveSlots] = useState<Record<string, string | null>>({});
  const [togglingTool, setTogglingTool] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<'all' | 'ai' | 'gec0'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const loadCatalog = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${CORE_API}/api/v1/marketplace/ai-tools?role=${role}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load tools (${res.status})`);
      }
      const data: AiToolsCatalogResponse = await res.json();
      setAllTools(data.tools || []);
      setActiveSlots(data.active_slots || {});
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load AI tools';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, role]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // Categorize tools by type
  const toolsByType: ToolsByType = useMemo(() => {
    const ai: AiTool[] = [];
    const gec0: AiTool[] = [];
    for (const tool of allTools) {
      const slotType = tool.meta?.slot_type || (tool.meta?.category?.toLowerCase().includes('gec0') ? 'gec0' : 'ai');
      if (slotType === 'gec0') {
        gec0.push(tool);
      } else {
        ai.push(tool);
      }
    }
    return { ai, gec0 };
  }, [allTools]);

  // Count active tools
  const activeCount = useMemo(() => {
    return Object.values(activeSlots).filter(Boolean).length;
  }, [activeSlots]);

  // Filter tools for display
  const filteredTools = useMemo(() => {
    let tools = allTools;

    // Filter by category (only for non-universal/teacher)
    if (!isUniversal) {
      if (filterCategory === 'ai') {
        tools = toolsByType.ai;
      } else if (filterCategory === 'gec0') {
        tools = toolsByType.gec0;
      }
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      tools = tools.filter((t) => {
        const label = (t.meta?.label || '').toLowerCase();
        const desc = (t.meta?.description || '').toLowerCase();
        const summary = (t.meta?.summary || '').toLowerCase();
        return label.includes(query) || desc.includes(query) || summary.includes(query);
      });
    }

    return tools;
  }, [allTools, toolsByType, filterCategory, searchQuery, isUniversal]);

  const isToolActive = (toolId: string): boolean => {
    return Object.values(activeSlots).includes(toolId);
  };

  const getToolSlotType = (tool: AiTool): 'ai' | 'gec0' => {
    return tool.meta?.slot_type === 'gec0' || tool.meta?.category?.toLowerCase().includes('gec0') ? 'gec0' : 'ai';
  };

  const canAddTool = (tool: AiTool): boolean => {
    if (isUniversal) {
      // Universal slots: just check total count
      return activeCount < config.total;
    }
    // Teacher: check per-type limits
    const slotType = getToolSlotType(tool);
    const typeLimit = slotType === 'gec0' ? (config.gec0 ?? 0) : (config.ai ?? 0);
    const typeCount = Object.entries(activeSlots).filter(([slotId, toolId]) => {
      if (!toolId) return false;
      if (slotType === 'gec0') return slotId.startsWith('gec0_');
      return slotId.startsWith('ai_');
    }).length;
    return typeCount < typeLimit;
  };

  const findAvailableSlot = (tool: AiTool): string | null => {
    if (isUniversal) {
      // Universal slots: use slot_1, slot_2, etc.
      for (let i = 1; i <= config.total; i++) {
        const slotId = `slot_${i}`;
        if (!activeSlots[slotId]) {
          return slotId;
        }
      }
      return null;
    }
    // Teacher: use type-specific slots
    const slotType = getToolSlotType(tool);
    const limit = slotType === 'gec0' ? (config.gec0 ?? 0) : (config.ai ?? 0);
    const prefix = slotType === 'gec0' ? 'gec0_slot_' : 'ai_slot_';

    for (let i = 1; i <= limit; i++) {
      const slotId = `${prefix}${i}`;
      if (!activeSlots[slotId]) {
        return slotId;
      }
    }
    return null;
  };

  const findSlotWithTool = (toolId: string): string | null => {
    for (const [slotId, tid] of Object.entries(activeSlots)) {
      if (tid === toolId) return slotId;
    }
    return null;
  };

  const toggleTool = async (tool: AiTool) => {
    if (!accessToken) return;

    setTogglingTool(tool.id);
    setError(null);
    setSuccessMsg(null);

    const isActive = isToolActive(tool.id);

    try {
      if (isActive) {
        // Remove tool
        const slotId = findSlotWithTool(tool.id);
        if (!slotId) throw new Error('Could not find slot for tool');

        const res = await fetch(`${CORE_API}/api/v1/marketplace/ai-tools/unassign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ role, slot: slotId }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to remove tool');

        setActiveSlots((prev) => {
          const next = { ...prev };
          delete next[slotId];
          return next;
        });
        setSuccessMsg(`${tool.meta?.label || 'Tool'} removed from your active tools`);
      } else {
        // Add tool
        if (!canAddTool(tool)) {
          throw new Error(`You've reached the tool limit (${config.total}). Remove a tool first.`);
        }

        const slotId = findAvailableSlot(tool);
        if (!slotId) throw new Error('No available slot');

        const res = await fetch(`${CORE_API}/api/v1/marketplace/ai-tools/assign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ role, slot: slotId, tool_id: tool.id }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Failed to add tool');

        setActiveSlots((prev) => ({ ...prev, [slotId]: tool.id }));
        setSuccessMsg(`${tool.meta?.label || 'Tool'} added to your active tools`);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to toggle tool';
      setError(message);
    } finally {
      setTogglingTool(null);
    }
  };

  // Auto-dismiss success message
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  if (loading) {
    return (
      <div className="space-y-6 p-4">
        <h1 className="text-2xl font-semibold text-cocoa">{title}</h1>
        <div className="text-sm text-cocoa/70">Loading available tools...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-cocoa">{title}</h1>
        <p className="mt-1 text-sm text-cocoa/70">{description}</p>
      </div>

      {/* Status Bar */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl bg-slate-50 p-4">
        {isUniversal ? (
          // Universal slots: single counter
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div>
              <span className="text-sm font-medium text-cocoa">Tool Slots</span>
              <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
                {activeCount} / {config.total}
              </span>
            </div>
          </div>
        ) : (
          // Separate AI and SmartBlock counters
          <>
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <span className="text-sm font-medium text-cocoa">AI Tools</span>
                <span className="ml-2 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                  {Object.entries(activeSlots).filter(([k, v]) => v && k.startsWith('ai_')).length} / {config.ai ?? 0}
                </span>
              </div>
            </div>

            {(config.gec0 ?? 0) > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <span className="text-sm font-medium text-cocoa">SmartBlock Trackers</span>
                  <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                    {Object.entries(activeSlots).filter(([k, v]) => v && k.startsWith('gec0_')).length} / {config.gec0 ?? 0}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {creditsAvailable !== null && (
          <div className="ml-auto flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <span className="text-sm font-medium text-cocoa">Credits</span>
              <span className="ml-2 text-sm font-semibold text-amber-700">
                {creditsLoading ? '...' : creditsAvailable}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-lg border border-brick/30 bg-brick/10 p-3 text-sm text-brick">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700">
          {successMsg}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {!isUniversal ? (
          // Teacher: show category filters
          <div className="flex rounded-lg border border-slate-200 bg-white p-1">
            <button
              onClick={() => setFilterCategory('all')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filterCategory === 'all'
                  ? 'bg-slate-100 text-cocoa'
                  : 'text-slate-500 hover:text-cocoa'
              }`}
            >
              All Tools
            </button>
            <button
              onClick={() => setFilterCategory('ai')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filterCategory === 'ai'
                  ? 'bg-sky-100 text-sky-800'
                  : 'text-slate-500 hover:text-cocoa'
              }`}
            >
              AI ({toolsByType.ai.length})
            </button>
            {(config.gec0 ?? 0) > 0 && (
              <button
                onClick={() => setFilterCategory('gec0')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  filterCategory === 'gec0'
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'text-slate-500 hover:text-cocoa'
                }`}
              >
                SmartBlocks ({toolsByType.gec0.length})
              </button>
            )}
          </div>
        ) : (
          // Universal slots: just show tool count
          <div className="text-sm text-cocoa/70">
            {allTools.length} tool{allTools.length !== 1 ? 's' : ''} available
          </div>
        )}

        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2 pl-10 text-sm focus:border-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-100"
          />
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Tools Catalog */}
      {filteredTools.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
          <div className="text-slate-400">
            {searchQuery ? 'No tools match your search' : 'No tools available yet'}
          </div>
          <p className="mt-2 text-sm text-slate-400">
            New tools for {role}s are coming soon.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredTools.map((tool) => {
            const active = isToolActive(tool.id);
            const slotType = getToolSlotType(tool);
            const isToggling = togglingTool === tool.id;
            const canAdd = canAddTool(tool);

            return (
              <ToolCatalogCard
                key={tool.id}
                tool={tool}
                slotType={slotType}
                isActive={active}
                isToggling={isToggling}
                canAdd={canAdd}
                onToggle={() => toggleTool(tool)}
                isUniversal={isUniversal}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

type ToolCatalogCardProps = {
  tool: AiTool;
  slotType: 'ai' | 'gec0';
  isActive: boolean;
  isToggling: boolean;
  canAdd: boolean;
  onToggle: () => void;
  isUniversal?: boolean;
};

function ToolCatalogCard({ tool, slotType, isActive, isToggling, canAdd, onToggle, isUniversal = false }: ToolCatalogCardProps) {
  // For universal slots, use a unified style
  const activeStyle = isActive
    ? isUniversal
      ? 'border-violet-400 bg-violet-50'
      : slotType === 'gec0'
        ? 'border-emerald-400 bg-emerald-50'
        : 'border-sky-400 bg-sky-50'
    : 'border-slate-200 bg-white';

  const iconStyle = isUniversal
    ? 'bg-violet-200 text-violet-800'
    : slotType === 'gec0'
      ? 'bg-emerald-200 text-emerald-800'
      : 'bg-sky-200 text-sky-800';

  const badgeStyle = isUniversal
    ? 'bg-violet-100 text-violet-700'
    : slotType === 'gec0'
      ? 'bg-emerald-100 text-emerald-700'
      : 'bg-sky-100 text-sky-700';

  const buttonStyle = isActive
    ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
    : isUniversal
      ? 'bg-violet-600 text-white hover:bg-violet-700'
      : slotType === 'gec0'
        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
        : 'bg-sky-600 text-white hover:bg-sky-700';

  return (
    <div
      className={`rounded-xl border-2 p-4 transition-all hover:shadow-md ${activeStyle}`}
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-3">
          {tool.meta?.icon_url ? (
            <img src={tool.meta.icon_url} alt="" className="h-12 w-12 rounded-xl object-cover shadow-sm" />
          ) : (
            <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${iconStyle}`}>
              <span className="text-sm font-bold">{slotType === 'gec0' ? 'SB' : 'AI'}</span>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-cocoa leading-tight">
              {tool.meta?.label || tool.meta?.summary || tool.id}
            </h3>
            <span className={`inline-block mt-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${badgeStyle}`}>
              {slotType === 'gec0' ? 'SmartBlock' : 'AI Tool'}
            </span>
          </div>
        </div>
        {isActive && (
          <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-xs font-medium text-white">
            Active
          </span>
        )}
      </div>

      {/* Description */}
      <p className="mb-3 text-sm text-cocoa/70 line-clamp-2">
        {tool.meta?.description || tool.meta?.surface_hint || 'Powerful tool for your workflow'}
      </p>

      {/* Surface hint */}
      {tool.meta?.surface_hint && tool.meta.description && (
        <p className="mb-3 text-xs italic text-slate-500">"{tool.meta.surface_hint}"</p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {(tool.tc_cost ?? 0) > 0 ? (
            <>
              <svg className="h-4 w-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-amber-700">{tool.tc_cost} TC</span>
            </>
          ) : (
            <span className="text-sm font-medium text-emerald-600">Free</span>
          )}
        </div>

        <button
          onClick={onToggle}
          disabled={isToggling || (!isActive && !canAdd)}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${buttonStyle}`}
        >
          {isToggling ? (
            <span className="flex items-center gap-1">
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {isActive ? 'Removing...' : 'Adding...'}
            </span>
          ) : isActive ? (
            'Remove'
          ) : !canAdd ? (
            'Slots Full'
          ) : (
            '+ Add'
          )}
        </button>
      </div>
    </div>
  );
}

export default RoleAiMarketplace;
